import { useEffect, useId, useRef, useState } from "react";
import { KeyRound, Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { ApiError } from "../../api/client";
import { stepUpApi, type StepUpEnrolment, type StepUpStatus } from "../../api/stepUpApi";

function messageOf(e: unknown): string {
  if (e instanceof ApiError) return e.status === 0 ? e.title : e.message || e.title;
  return "Something went wrong. Try again.";
}

/**
 * The authenticator-app step-up (Q-O12; ported from Summit Engineering's MfaChallenge): first time — scan the QR code (or
 * type the key), then enter the code; afterwards — the 6-digit code for this sign-in. The API checks the code and records
 * the proof against this sign-in; nothing here decides access. Used by the "Confirm it's you" page and the in-app prompt.
 */
export function StepUpChallenge({
  onVerified,
  autoFocus = true,
  requireFreshCode = false
}: {
  onVerified: (status: StepUpStatus) => void;
  autoFocus?: boolean;
  /** O-R90: signing has been idle for an hour — ask for a new code even though this sign-in shows as verified. */
  requireFreshCode?: boolean;
}) {
  const [status, setStatus] = useState<StepUpStatus | null>(null);
  const [enrolment, setEnrolment] = useState<StepUpEnrolment | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const codeId = useId();
  const hintId = useId();

  useEffect(() => {
    let cancelled = false;
    stepUpApi
      .status()
      .then((s) => {
        if (cancelled) return;
        setStatus(s);
        if (s.verified && !(requireFreshCode && s.enrolled && !s.bypassed)) onVerified(s);
      })
      .catch((e: unknown) => !cancelled && setError(messageOf(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoFocus && status && (status.enrolled || enrolment)) inputRef.current?.focus();
  }, [autoFocus, status, enrolment]);

  const showQr = async () => {
    setBusy(true);
    setError(null);
    try {
      setEnrolment(await stepUpApi.enrol());
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) {
      setError("Enter the 6-digit code shown in your authenticator app.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await stepUpApi.verify(digits);
      setStatus(result);
      onVerified(result);
    } catch (e) {
      setError(messageOf(e));
      setCode("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  if (!status && !error) {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-600" role="status">
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
        Checking your sign-in…
      </p>
    );
  }

  const needsSetUp = status !== null && !status.enrolled;
  const canEnterCode = status !== null && (status.enrolled || enrolment !== null);

  return (
    <div className="space-y-4" data-testid="step-up-challenge">
      {requireFreshCode && status?.enrolled && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="step-up-signing-idle">
          You have not signed anything for an hour. Enter a new code from your authenticator app to sign.
        </p>
      )}
      {needsSetUp && !enrolment && (
        <div className="space-y-3" data-testid="step-up-setup-intro">
          <p className="flex items-start gap-2 text-sm text-slate-700">
            <Smartphone aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none text-brand-600" />
            <span>
              Roles that approve, publish or administer (Admin, Operations Manager, Planner and the approvers) confirm a code from an authenticator app each
              time they sign in. Set it up once with Microsoft Authenticator (or any authenticator app) on your phone.
            </span>
          </p>
          <button type="button" className="btn-primary flex w-full items-center justify-center gap-2" onClick={() => void showQr()} disabled={busy}>
            <KeyRound aria-hidden="true" className="h-4 w-4" />
            Set up my authenticator app
          </button>
        </div>
      )}

      {enrolment && (
        <div className="space-y-3" data-testid="step-up-enrolment">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
            <li>In your authenticator app, add an account and scan this code.</li>
            <li>Enter the 6-digit code the app shows for &ldquo;{enrolment.issuer}&rdquo;.</li>
          </ol>
          <div className="flex justify-center rounded-lg border border-slate-200 bg-white p-3">
            <img src={enrolment.qrCodePngDataUri} alt={`QR code to add ${enrolment.account} to your authenticator app`} width={184} height={184} />
          </div>
          <p className="text-xs text-slate-600">
            Cannot scan? Enter this key by hand (time-based):{" "}
            <code className="select-all break-all rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] text-slate-800" data-testid="step-up-manual-key">
              {enrolment.manualKey}
            </code>
          </p>
        </div>
      )}

      {canEnterCode && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <div>
            <label htmlFor={codeId} className="block text-sm font-medium text-slate-800">
              6-digit code
            </label>
            <input
              id={codeId}
              ref={inputRef}
              className="input mt-1 w-40 text-center font-mono text-lg tracking-[0.3em]"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              aria-describedby={hintId}
              aria-invalid={!!error}
              disabled={busy}
              data-testid="step-up-code"
            />
            <p id={hintId} className="mt-1 text-xs text-slate-500">
              From your authenticator app — a new code appears every 30 seconds.
            </p>
          </div>
          <button type="submit" className="btn-primary flex w-full items-center justify-center gap-2" disabled={busy} data-testid="step-up-verify">
            <ShieldCheck aria-hidden="true" className="h-4 w-4" />
            {busy ? "Checking…" : "Verify"}
          </button>
        </form>
      )}

      {error && (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      )}

      <p className="text-xs text-slate-500">
        Lost your phone? Another Admin can reset your authenticator under Administration › Users &amp; roles. Summit Operations never asks for this code by
        email or phone.
      </p>
    </div>
  );
}
